import * as Fathom from 'fathom-client';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import ReactGA from 'react-ga';
import { Provider } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor, store } from './utils/store';

// import configureStore from './utils/store';

import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import DefaultLandingRoute from './components/DefaultLandingRoute';
// import Footer from './components/Footer';
import ActivityLoader from './components/ActivityLoader';
import Parent from './components/Parent';
import ErrorBoundary from './pages/ErrorBoundary';
import NoMatch404 from './pages/NoMatch404';
import { ChatWidgetProvider } from './contexts/ChatWidgetContext';
import ChatWidget from './components/chat/ChatWidget';
import { isEnableDemoFeatures } from './utils/CTUtils';
import { DOMESTIC_HOME_PATH } from './components/domestic/header/domesticNavPermissions';
import { CONTACT_CSR_PATH } from './components/domestic/contactCsr/contactCsrForm';
import { MY_SERVICES_PATH } from './components/domestic/myServices/myServicesCatalog';
import { injectStore } from './utils/http';
import Loadable from './utils/loadable';
import QueryProvider from './QueryProvider';

// inject store to http api
injectStore(store);

const Login = Loadable({
  loader: () => import('./pages/Login'),
  loading: ActivityLoader,
});
const PreviewLogin = Loadable({
  loader: () => import('./pages/authPreview/PreviewLogin'),
  loading: ActivityLoader,
});
const PreviewRegister = Loadable({
  loader: () => import('./pages/authPreview/PreviewRegister'),
  loading: ActivityLoader,
});
const VcscRegister = Loadable({
  loader: () => import('./pages/vcscRegister/VcscRegister'),
  loading: ActivityLoader,
});
const PreviewVerify = Loadable({
  loader: () => import('./pages/authPreview/PreviewVerify'),
  loading: ActivityLoader,
});
const PreviewLegacyHome = Loadable({
  loader: () => import('./pages/authPreview/PreviewLegacyHome'),
  loading: ActivityLoader,
});
const PreviewIndex = Loadable({
  loader: () => import('./pages/domesticPreview/PreviewIndex'),
  loading: ActivityLoader,
});
const LoginToken = Loadable({
  loader: () => import('./pages/LoginToken'),
  loading: ActivityLoader,
});
const SSOLogin = Loadable({
  loader: () => import('./pages/SSOLogin'),
  loading: ActivityLoader,
});
const SSOReturn = Loadable({
  loader: () => import('./pages/SSOReturn'),
  loading: ActivityLoader,
});
const ConfirmUser = Loadable({
  loader: () => import('./pages/ConfirmUser'),
  loading: ActivityLoader,
});
const ResetPassword = Loadable({
  loader: () => import('./pages/ResetPassword'),
  loading: ActivityLoader,
});
const DocuSignReturn = Loadable({
  loader: () => import('./pages/DocuSignReturn'),
  loading: ActivityLoader,
});
const ForgotPassword = Loadable({
  loader: () => import('./pages/ForgotPassword'),
  loading: ActivityLoader,
});
const ChangePassword = Loadable({
  loader: () => import('./pages/ChangePassword'),
  loading: ActivityLoader,
});
const ContainerSearch = Loadable({
  loader: () => import('./pages/ContainerSearch'),
  loading: ActivityLoader,
});
const ContainerList = Loadable({
  loader: () => import('./pages/ContainerList'),
  loading: ActivityLoader,
});
const ContainerDashboard = Loadable({
  loader: () => import('./pages/ContainerDashboard'),
  loading: ActivityLoader,
});
const BillingDashboard = Loadable({
  loader: () => import('./pages/BillingDashboard'),
  loading: ActivityLoader,
});
const BookingDashboard = Loadable({
  loader: () => import('./pages/BookingDashboard'),
  loading: ActivityLoader,
});
const BlanketLOI = Loadable({
  loader: () => import('./pages/BlanketLOI'),
  loading: ActivityLoader,
});
const BlanketLOIDetails = Loadable({
  loader: () => import('./pages/BlanketLOIDetails'),
  loading: ActivityLoader,
});
const BookingRequest = Loadable({
  loader: () => import('./pages/BookingRequest'),
  loading: ActivityLoader,
});
const OcpRequest = Loadable({
  loader: () => import('./pages/OcpRequest'),
  loading: ActivityLoader,
});
const OcpRequestDetails = Loadable({
  loader: () => import('./pages/OcpRequestDetails'),
  loading: ActivityLoader,
});
const BookingRequestDetails = Loadable({
  loader: () => import('./pages/BookingRequestDetails'),
  loading: ActivityLoader,
});
const BookingUpdate = Loadable({
  loader: () => import('./pages/BookingUpdate'),
  loading: ActivityLoader,
});
const BookingUpdateDetails = Loadable({
  loader: () => import('./pages/BookingUpdateDetails'),
  loading: ActivityLoader,
});
const BookingHistory = Loadable({
  loader: () => import('./pages/BookingHistory'),
  loading: ActivityLoader,
});
const BookingCompare = Loadable({
  loader: () => import('./pages/BookingComparison'),
  loading: ActivityLoader,
});
const ShippingInstructionCompare = Loadable({
  loader: () => import('./pages/ShippingInstructionComparison'),
  loading: ActivityLoader,
});
const ContainerTracking = Loadable({
  loader: () => import('./pages/ContainerTracking'),
  loading: ActivityLoader,
});
const Registration = Loadable({
  loader: () => import('./pages/Registration'),
  loading: ActivityLoader,
});
const RegistrationInfo = Loadable({
  loader: () => import('./pages/RegistrationInfo'),
  loading: ActivityLoader,
});
const UserProfile = Loadable({
  loader: () => import('./pages/UserProfile'),
  loading: ActivityLoader,
});
const InfoCenter = Loadable({
  loader: () => import('./pages/InformationCenter'),
  loading: ActivityLoader,
});
const AdminProfileSearch = Loadable({
  loader: () => import('./pages/AdminProfileSearch'),
  loading: ActivityLoader,
});
const AdminProfileSearchDetails = Loadable({
  loader: () => import('./pages/AdminProfileSearchDetails'),
  loading: ActivityLoader,
});
const AdminAccountSearch = Loadable({
  loader: () => import('./pages/AdminAccountSearch'),
  loading: ActivityLoader,
});
const AdminAccountSearchDetails = Loadable({
  loader: () => import('./pages/AdminAccountSearchDetails'),
  loading: ActivityLoader,
});
const SupportHelp = Loadable({
  loader: () => import('./pages/SupportHelp'),
  loading: ActivityLoader,
});
const ShippingInstructions = Loadable({
  loader: () => import('./pages/ShippingInstructions'),
  loading: ActivityLoader,
});
const ShippingInstructionsDetails = Loadable({
  loader: () => import('./pages/ShippingInstructionsDetails'),
  loading: ActivityLoader,
});
const ShippingInstructionsTemplateDetails = Loadable({
  loader: () => import('./pages/ShippingInstructionsTemplateDetails'),
  loading: ActivityLoader,
});
const AdminEtaRuleSearch = Loadable({
  loader: () => import('./pages/AdminEtaRuleSearch'),
  loading: ActivityLoader,
});
const AdminEtaRuleSearchDetails = Loadable({
  loader: () => import('./pages/AdminEtaRuleSearchDetails'),
  loading: ActivityLoader,
});

const SalesView = Loadable({
  loader: () => import('./pages/SalesView'),
  loading: ActivityLoader,
});
const RepSalesView = Loadable({
  loader: () => import('./pages/RepSalesView'),
  loading: ActivityLoader,
});

const AdminEtaVvdExSearch = Loadable({
  loader: () => import('./pages/AdminEtaVvdExSearch'),
  loading: ActivityLoader,
});
const AdminEtaVvdExSearchDetails = Loadable({
  loader: () => import('./pages/AdminEtaVvdExSearchDetails'),
  loading: ActivityLoader,
});

const VGMDetails = Loadable({
  loader: () => import('./pages/VGMDetails'),
  loading: ActivityLoader,
});

const DetentionChargeCalculator = Loadable({
  loader: () => import('./pages/DetentionChargeCalculator'),
  loading: ActivityLoader,
});

const DomesticShipmentHub = Loadable({
  loader: () => import('./components/domestic/DomesticShipmentHub'),
  loading: ActivityLoader,
});
const DomesticBookingHub = Loadable({
  loader: () => import('./components/domestic/DomesticBookingHub'),
  loading: ActivityLoader,
});
const DomesticIntlShipmentHub = Loadable({
  loader: () => import('./components/domestic/DomesticIntlShipmentHub'),
  loading: ActivityLoader,
});
const DomesticShippingInstructionHub = Loadable({
  loader: () =>
    import('./components/domestic/DomesticShippingInstructionHub'),
  loading: ActivityLoader,
});
const DomesticAccountStatementHub = Loadable({
  loader: () => import('./components/domestic/DomesticAccountStatementHub'),
  loading: ActivityLoader,
});
const DomesticDocumentHub = Loadable({
  loader: () => import('./components/domestic/DomesticDocumentHub'),
  loading: ActivityLoader,
});
const DomesticHome = Loadable({
  loader: () => import('./pages/domestic/DomesticHome'),
  loading: ActivityLoader,
});
const DomesticContactCsr = Loadable({
  loader: () => import('./pages/domestic/DomesticContactCsr'),
  loading: ActivityLoader,
});
const DomesticMyServices = Loadable({
  loader: () => import('./pages/domestic/DomesticMyServices'),
  loading: ActivityLoader,
});

function DomesticLegacyTrackingRedirect() {
  return <Redirect to="/d/shipment/dashboard" />;
}

// import styles from './App.module.css';

global.BMapKey = import.meta.env.REACT_APP_BING_MAP_KEY;

//  User Directory Service APIs
global.URL_UserReg = import.meta.env.REACT_APP_USER_API_URL + '/dir/user';
global.URL_SwitchValidate = import.meta.env.REACT_APP_USER_API_URL + '/dir/switch/validate';
global.URL_SwitchRegister = import.meta.env.REACT_APP_USER_API_URL + '/dir/switch/register';
global.URL_UserConf =
  import.meta.env.REACT_APP_USER_API_URL + '/dir/user/confirmation';
global.URL_UserForgot = import.meta.env.REACT_APP_USER_API_URL + '/dir/user/forgot';
global.URL_ResetPass =
  import.meta.env.REACT_APP_USER_API_URL + '/dir/user/confirmforgot';
global.URL_Login = import.meta.env.REACT_APP_USER_API_URL + '/dir/login';
global.URL_SSO_Callback =
  import.meta.env.REACT_APP_USER_API_URL + '/dir/sso/callback';
global.ENABLE_SSO_LOGIN =
  String(import.meta.env.REACT_APP_ENABLE_SSO_LOGIN || 'false').toLowerCase() === 'true';
global.URL_CompleteNewPassword =
  import.meta.env.REACT_APP_USER_API_URL + '/dir/complete-new-password';
global.URL_TokenRefresh =
  import.meta.env.REACT_APP_USER_API_URL + '/dir/login/refresh';
global.URL_Logout = import.meta.env.REACT_APP_USER_API_URL + '/dir/logout';
global.URL_ChangePass = import.meta.env.REACT_APP_USER_API_URL + '/dir/user/change';
global.URL_ResendCode = import.meta.env.REACT_APP_USER_API_URL + '/dir/user/resend';

// User Management Service APIs
global.URL_UserProfileLookupUpdate =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/cp/user';
global.URL_AdminUserReset =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/reset';
global.URL_AdminUserSetPassword =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/pwd/set';
global.URL_AdminEmailLookUp =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/email';
global.URL_AdminUserSearch =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/user';
global.URL_AdminUserDetail =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/user/authEvents';
global.URL_AdminOrgLookUp =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/cp/org';
global.URL_AdminProfileSearch =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/admin/cp/user';
global.URL_UserAssume =
  import.meta.env.REACT_APP_USER_API_URL + '/mgmt/user/assume';

// CargoTrack API's
global.URL_Base = import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/';
global.V2_URL_Base = import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/';
global.URL_Base_Proxy = global.V2_URL_Base + 'gl/';
global.URL_Base_Public_Proxy = global.V2_URL_Base + 'glusp/v/';
global.URL_SearchByBooking =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo?';
global.URL_SearchByBookingV2 =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/cargo?';
// Domestic shipment-tracking short/full Excel (POST, binary via secured proxy)
global.URL_CargoSearchShortExcel =
  global.V2_URL_Base + 'cargo/search/shortexcel';
global.URL_CargoSearchFullExcel =
  global.V2_URL_Base + 'cargo/search/fullexcel';
global.URL_SearchDetails =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/detail?';
global.URL_SearchByBookingNoAuth =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargopub?';
global.URL_SearchDetailsNoAuth =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargopub/detailpub?';
global.URL_GetVVDs =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/vvds';
global.URL_GetVVDsAll =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/vvds/all';
global.URL_GetCardData =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/dashboard/';
global.URL_GetLocation =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/location?';
global.URL_BookingSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings';
global.URL_OcpSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/requests';
global.URL_OcpRequest =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/request';
global.URL_OcpSave =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/save';
global.URL_OcpVerify =
import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/verify';
global.URL_OcpCancel =
import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/cancel';
global.URL_OcpLocationRefData =
 import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/refdata/locations';
global.URL_OcpLocationDepotRefData =
 import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ocp/refdata/location-depots';
global.URL_BookingRequestRefData =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/refdata';
global.URL_BookingRequestRefDataNoAuth =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/pub/refdata/';
global.URL_TranslateRefData =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/cargo/translateRefData';
global.URL_BookingDetail =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking';
global.URL_BookingUpdateDetail =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/view'; // '/gates/booking' /gates-service/api/v1/booking/
global.URL_BookingSaveDraft =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/draft';
global.URL_BookingConfirmation =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/confirmation';
global.URL_VesselSearchByVVD =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/vessel/vvd?';
global.URL_VesselSearchByDate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/vessel/date?';
global.URL_GetBRGridPreference =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/prefs?';
global.URL_ViewBooking =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/domesticbooking/view-booking';
global.URL_DomesticBookingSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/domesticbooking/search';
//Upload file to S3 is commented out - logic to move to backend
// global.URL_BookingUpload =
//   import.meta.env.REACT_APP_BOOKING_UPLOAD_URL + '/cargotrack/edi/form/url';
global.URL_ShippingSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions';
global.URL_BiSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/search';
global.URL_BiBooking =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/booking';
global.URL_BiInstruction =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions';
global.URL_BiSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions';
global.URL_BiValidate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/validate';
global.URL_BiPreview =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/billinginstructions/preview';
global.URL_BookingRefValidation =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings/validate';
global.URL_ShippingDetail =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instruction';
global.URL_ShippingContainerLookup =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/containers';
global.URL_CustomsHistory =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/cargo/customs/history';
global.URL_ShippingSaveDraft =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/draft';
global.URL_ShippingSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si';
global.URL_SITemplateCreate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/si/template/create';
global.URL_SITemplateList =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/si/template/list';
global.URL_DomesticBookingTemplateList =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/templates';
global.URL_DomesticBookingLoad =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/load/v2';
global.URL_DomesticBookingRenameMyTemplate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/rename-my-template';
global.URL_DomesticBookingSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/submit';
global.URL_DomesticBookingValidate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/validate';
global.URL_DomesticBookingDraft =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/draft';
global.URL_DomesticBookingCancel =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/cancel';
global.URL_DomesticBookingReactivate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/reactivate';
global.URL_DomesticBookingSaveAsTemplate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/save-as-template';
global.URL_DomesticBookingVesselItineraries =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/vessel-itineraries';
global.URL_DomesticBookingVesselItinerariesV2 =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/vessel-itineraries/v2';
global.URL_DomesticBookingDecodeVin =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/decode-vin';
global.URL_DomesticBookingCalculatePickupDate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/calculate-pickup-date';
global.URL_DomesticBookingPovBlockDates =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/pov-block-dates';
global.URL_DomesticBookingEquipmentRef =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/equipment';
global.URL_DomesticBookingPortCities =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/cities';
global.URL_DomesticBookingLoadDischargeRef =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/load-discharge';
global.URL_DomesticBookingRailRamps =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/rail-ramps';
global.URL_DomesticBookingPackageTypes =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/package-types';
global.URL_DomesticBookingSpotAddresses =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/spot-addresses';
global.URL_DomesticBookingDetailsDashboardSearch =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/dashboard/search';
global.URL_DomesticBookingDetailsDashboardBooking =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/dashboard/bookings';
global.URL_DomesticAccountBalancesSummary =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/accountbalances/summary';
global.URL_DomesticAccountBalancesDetails =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/accountbalances/details';
global.URL_DomesticContactCsr =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/domestic/contact-csr';
global.URL_DomesticMyServicesRequest =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domestic/my-services/request';
global.URL_DomesticBookingSpotAddressesExpand =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/domesticbooking/ref/spot-addresses/expand';
// AutoTrack (Track Autos) APIs - POST, served under the V2 base
// (https://api.cargo.<env>.matson.com/v2/cargotrack/ocean/tracking/autotrack/*)
global.URL_TrackAutosReport =
  global.V2_URL_Base + 'ocean/tracking/autotrack/report';
global.URL_TrackAutosTotals =
  global.V2_URL_Base + 'ocean/tracking/autotrack/totals';
global.URL_TrackAutosInlandTotals =
  global.V2_URL_Base + 'ocean/tracking/autotrack/inland-totals';
global.URL_TrackAutosExport =
  global.V2_URL_Base + 'ocean/tracking/autotrack/export';
global.URL_TrackAutosStatusOptions =
  global.V2_URL_Base + 'ocean/tracking/autotrack/status-options';
global.URL_BookingTemplateList =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/booking/templates';
global.URL_BookingSaveSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bookings/forms';
global.URL_SIFormDownload =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/form';
global.URL_SIBLDraft =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/bldraft';
global.URL_SIBLDocsSend =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/blDocsSend';
global.URL_SIExcelSaveSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/forms';
global.URL_SIExcelValidate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/validate';
global.URL_SIExcelParseUpload =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/si/instructions/xlsparse';
global.countryCode = import.meta.env.REACT_APP_COUNTRY_CODE;
global.URL_GetExtTokenInfo =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/ext/gettoken';
global.URL_ManualAddition =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/manual';
global.URL_AllocationHistory =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/report/download';
global.URL_HISTORICAL_VVD_REF =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/historical-vvd';
// ETA apis
global.URL_EtaRule_Search =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/searchPost';
global.URL_EtaRule_Details =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/createUpdate';

global.URL_EtaExceptionRule_Search =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/exception/search';
global.URL_EtaExceptionRule_Details =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/cargotrack/maintenance/eta/exception/createUpdate';
// VGM apis
global.URL_VGMGetCarrierBookingTypes =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/getcarriers';
global.URL_VGMSearchByBooking =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/search';
global.URL_VGMSaveBkEquipments =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/save';
global.URL_VGMSaveMultipleBkEquipments =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vgm/saveMultipleBooking';
global.URL_VGMValidContainer =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vgm/isvalidcontainer';

// Bldoc apis
global.URL_BldocSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/search';
global.URL_BldocOTLoiSubmit =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/onetimeloi';
global.URL_BldocBlanketLoiSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/search';
global.URL_BldocBlanketLoiAdd =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/add';
global.URL_BldocBlanketLoiUpdate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/update';
global.URL_BldocBlanketLoiGet =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/get';
global.URL_BldocBlanketLoiTariff =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/blanketloi/tariff';
global.URL_BldocDownload =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/download';
global.URL_BldocStatusHistory =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/statushistory';
global.URL_BldocDownloadUrl =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/bldoc/downloadloi';
global.URL_BldocBlanketShipperApproval =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/blanket';
global.URL_BldocOnetimeShipperApproval =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/onetime';
global.URL_BldocSignComplete =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/complete';
global.URL_BldocSignCheckCallbackStatus =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/checkcallback';
global.URL_BldocSignCheckgroup =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/doc/sign/checkgroup';

global.URL_SalesViewSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/search';
global.URL_SalesViewUpdate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/update';
global.URL_SalesViewRepVVD =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/vvd';
global.URL_SalesViewRepPort =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/pol';
global.URL_SalesViewRepSalesRep =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/salesRep';
global.URL_RepSalesViewAccountName =
  import.meta.env.REACT_APP_CARGO_API_URL + '/cargotrack/vvdAllocation/account';
global.URL_RepSalesViewSearch =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/search';
global.URL_RepSalesViewUpdate =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/update';
global.URL_RepSalesViewDownload =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/download';
global.URL_RepSalesViewDownloadV2 =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/download/v2';
global.URL_RepSalesViewUpload =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/upload';
global.URL_RepSalesViewUploadV2 =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/vesselAllocation/upload/v2';
global.URL_RepSalesViewNotify =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/notify';
global.URL_RepSalesViewHistory =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/history';
global.URL_RepSalesViewRepDefault =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/vesselAllocation/salesRep/default';
global.URL_EirHistory =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/booking/eir/history';
global.URL_BookingCorrSearch =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/search';
global.URL_BookingCorrHistory =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/history';
global.URL_BookingCorrCompare =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/compare';
global.URL_BookingCorrList =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/';
// global.URL_ShippingInstructionCorrCompare =
//   import.meta.env.REACT_APP_CARGO_API_URL +
//   '/v2/cargotrack/gl/siRequest/compare';
global.URL_ShippingInstructionVersionList =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/siRequest/version';
global.URL_ShippingInstructionCorrApprove =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/gl/siRequest/siCorrApprove';
global.URL_BookingCorrController =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/action';
global.URL_BookingCorrControllerUpdate =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/bookingcorr/update';
global.URL_ShippingInstructionCorrCompare =
  import.meta.env.REACT_APP_CARGO_API_URL + '/v2/cargotrack/gl/siRequest/compare';
global.URL_SiCustomsDocDownload =
  import.meta.env.REACT_APP_CARGO_API_URL +
  '/v2/cargotrack/dl/siRequest/downloadCustomsDoc';
global.URL_BookingCorrCancelBooking =
  import.meta.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/deleteBooking";
global.URL_BookingCorrReApproveBooking =
  import.meta.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/reApproveBooking";
global.URL_BookingCorrPermissions =
  import.meta.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/checkPermission";
global.URL_BookingCorrIsLatest =
  import.meta.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/gl/bookingcorr/checkLatestBookingData";
global.URL_AVPNotificationSubscription =
  import.meta.env.REACT_APP_CARGO_API_URL +
  "/v2/cargotrack/pubapi/v1/trucker/subscriptions";
// Detention Charge Calc API/s
global.URL_DETENTION_RATE =
  import.meta.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/detention/rate";
global.URL_DETENTION_RATE_PUBLIC =
  import.meta.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/detention/rate/pub";
global.URL_ServiceContractRefData =
  import.meta.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/gl/detention/svcContract";
global.URL_VGMRefValidation =
  import.meta.env.REACT_APP_CARGO_API_URL + "/v2/cargotrack/gl/vgm/validate";
global.URL_Health = global.URL_Base_Public_Proxy + "util/health";
//
//
// load analytics service based on country.
// Needed because GA does not work in CN
//
if (
  'CN' === import.meta.env.REACT_APP_COUNTRY_CODE ||
  'sandbox' === import.meta.env.REACT_APP_STAGE
) {
  console.debug('- load fathom/');
  Fathom.load(import.meta.env.REACT_APP_FATHOM_SITE_ID);
}
if ('US' === import.meta.env.REACT_APP_COUNTRY_CODE) {
  console.debug('- init ga');
  ReactGA.initialize(import.meta.env.REACT_APP_GA_TRACKER);
}

global.trackAnalyticsPageView = (pageurl) => {
  if (import.meta.env.REACT_APP_COUNTRY_CODE === 'CN') {
    console.info('- track fathom/ page view');
    Fathom.trackPageview({ pageurl });
  } else {
    console.info('- track ga page view');
    ReactGA.pageview(pageurl);
  }
};

global.trackAnalyticsEvent = (category, action, pageurl, user) => {
  console.debug(
    `Analytics Event - category: ${category} action: ${action} pageurl: ${pageurl} user: ${user}`
  );
  if (import.meta.env.REACT_APP_COUNTRY_CODE === 'CN') {
    console.info('- send fathom/ event');
    console.log(`---- action: ${action}`);
    Fathom.trackGoal(action, 0);
  } else {
    console.info('- send ga event');
    ReactGA.event({ category, action, label: user });
  }
};

global.updateUserObject = (user, response) => {
  let newUser = _.cloneDeep(user);
  newUser.idToken = response.IdToken;
  newUser.accessToken = response.AccessToken;
  newUser.expiresIn = response.ExpiresIn;
  newUser.tokenType = response.TokenType;
  newUser.loginTimeStamp = moment().toDate();
  if (response.RefreshToken) {
    newUser.refreshToken = response.RefreshToken;
  }
  return newUser;
};

const App = () => {
  //useEffect(() => {
  //    document.title = "Matson Cargo Portal " + "("+ store.getState().appinfo.instanceId + ")";
  //  }, []);

  return (
    <Provider store={store}>
      <QueryProvider>
      <PersistGate loading={null} persistor={persistor}>
        <Router>
          <ChatWidgetProvider>
            <Parent>
              <Header />
              <ErrorBoundary>
                <Switch>
                <Route exact path="/" component={DefaultLandingRoute} />
                <Route exact path="/signin" component={Login} />
                {isEnableDemoFeatures() && (
                  <Route exact path="/preview/signin" component={PreviewLogin} />
                )}
                {isEnableDemoFeatures() && (
                  <Route exact path="/preview/register" component={PreviewRegister} />
                )}
                <Route exact path="/vcsc/register" component={VcscRegister} />
                {isEnableDemoFeatures() && (
                  <Route exact path="/preview/verify" component={PreviewVerify} />
                )}
                {isEnableDemoFeatures() && (
                  <Route exact path="/preview/legacy-home" component={PreviewLegacyHome} />
                )}
                {isEnableDemoFeatures() && (
                  <Route exact path="/preview/mockups" component={PreviewIndex} />
                )}
                {!isEnableDemoFeatures() && (
                  <Redirect from="/preview" to="/signin" />
                )}
                <Route
                  exact
                  path="/ssologin"
                  render={(props) =>
                    global.ENABLE_SSO_LOGIN ? (
                      <SSOLogin {...props} />
                    ) : (
                      <Redirect
                        to={{
                          pathname: '/signin',
                          state: {
                            ssoError:
                              'SSO login is disabled for this environment. Please use username/password login.',
                          },
                        }}
                      />
                    )
                  }
                />
                <Route
                  exact
                  path="/sso/okta"
                  render={(props) =>
                    global.ENABLE_SSO_LOGIN ? (
                      <SSOReturn {...props} />
                    ) : (
                      <Redirect to="/signin" />
                    )
                  }
                />
                <Route
                  exact
                  path="/detentionChargeCalculator"
                  component={DetentionChargeCalculator}
                />
                <Route
                  exact
                  path="/signintoken/:username/:token"
                  component={LoginToken}
                />
                <Route
                  exact
                  path="/confirmUser/:username"
                  component={ConfirmUser}
                />
                <Route
                  exact
                  path="/confirmUser/:username/:code"
                  component={ConfirmUser}
                />
                <Route
                  exact
                  path="/resetPassword/:username"
                  component={ResetPassword}
                />
                <Route
                  exact
                  path="/resetPassword/:username/:code"
                  component={ResetPassword}
                />
                <Route
                  exact
                  path="/forgetPassword"
                  component={ForgotPassword}
                />
                <Route exact path="/registration" component={Registration} />
                <Route exact path="/registration-info" component={RegistrationInfo} />
                <Route
                  exact
                  path="/informationCenter"
                  component={InfoCenter}
                />
                <Route
                  exact
                  path="/containerList/:searchType=:searchQuery"
                  component={ContainerList}
                />
                <Route
                  exact
                  path="/containerDetails/:bookingNo/:containerNo"
                  component={ContainerTracking}
                />
                <Route exact path="/vgmDetails" component={VGMDetails} />
                <Route
                  exact
                  path="/docuSignReturn"
                  component={DocuSignReturn}
                />
                <ProtectedRoute
                  exact
                  path="/search"
                  component={ContainerSearch}
                />
                <ProtectedRoute
                  exact
                  path="/containerdash"
                  component={ContainerDashboard}
                />
                <ProtectedRoute
                  exact
                  path="/billingdoc"
                  component={BillingDashboard}
                />
                <ProtectedRoute exact path="/salesView" component={SalesView} />
                <ProtectedRoute
                  exact
                  path="/repSalesView"
                  component={RepSalesView}
                />
                <ProtectedRoute
                  exact
                  path="/bookingdash"
                  component={BookingDashboard}
                />
                <ProtectedRoute
                  exact
                  path="/blanketloi"
                  component={BlanketLOI}
                />
                <ProtectedRoute
                  exact
                  path="/blanketloidetails"
                  component={BlanketLOIDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookingRequest"
                  component={BookingRequest}
                />
                {isEnableDemoFeatures() && (
                  <ProtectedRoute
                    exact
                    path="/ocpRequest"
                    component={OcpRequest}
                  />
                )}
                {isEnableDemoFeatures() && (
                  <ProtectedRoute
                    exact
                    path="/ocpRequestDetails"
                    component={OcpRequestDetails}
                  />
                )}
                <ProtectedRoute
                  exact
                  path="/bookingRequestDetails"
                  component={BookingRequestDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookingUpdate"
                  component={BookingUpdate}
                />
                <ProtectedRoute
                  exact
                  path="/bookingUpdateDetails"
                  component={BookingUpdateDetails}
                />
                <ProtectedRoute
                  exact
                  path="/bookinghistory"
                  component={BookingHistory}
                />
                <ProtectedRoute
                  exact
                  path="/bookingCompare"
                  component={BookingCompare}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionCompare"
                  component={ShippingInstructionCompare}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructions"
                  component={ShippingInstructions}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionsDetails"
                  component={ShippingInstructionsDetails}
                />
                <ProtectedRoute
                  exact
                  path="/shippingInstructionsTemplateDetails"
                  component={ShippingInstructionsTemplateDetails}
                />
                <ProtectedRoute exact path="/profile" component={UserProfile} />
                <ProtectedRoute
                  exact
                  path="/adminAccountSearch"
                  component={AdminAccountSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminAccountSearchDetails/:username"
                  component={AdminAccountSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/adminProfileSearch"
                  component={AdminProfileSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminProfileSearchDetails/:username"
                  component={AdminProfileSearchDetails}
                />
                <ProtectedRoute exact path="/support" component={SupportHelp} />
                <ProtectedRoute
                  exact
                  path="/changePassword"
                  component={ChangePassword}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleSearch"
                  component={AdminEtaRuleSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleSearchDetails/:ruleId"
                  component={AdminEtaRuleSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleExceptionSearch"
                  component={AdminEtaVvdExSearch}
                />
                <ProtectedRoute
                  exact
                  path="/adminEtaRuleExceptionDetails/:ruleId"
                  component={AdminEtaVvdExSearchDetails}
                />
                <ProtectedRoute
                  exact
                  path="/detentionChargeCalculator"
                  component={DetentionChargeCalculator}
                />
                <ProtectedRoute
                  exact
                  path={DOMESTIC_HOME_PATH}
                  component={DomesticHome}
                />
                <ProtectedRoute
                  exact
                  path={CONTACT_CSR_PATH}
                  component={DomesticContactCsr}
                />
                <ProtectedRoute
                  exact
                  path={MY_SERVICES_PATH}
                  component={DomesticMyServices}
                />
                <ProtectedRoute
                  path="/d/booking"
                  component={DomesticBookingHub}
                />
                <ProtectedRoute
                  path="/d/shipping-instruction"
                  component={DomesticShippingInstructionHub}
                />
                <ProtectedRoute
                  path="/d/billingdocs"
                  component={DomesticDocumentHub}
                />
                <ProtectedRoute
                  path="/d/shipment"
                  component={DomesticShipmentHub}
                />
                <ProtectedRoute
                  path="/d/intl"
                  component={DomesticIntlShipmentHub}
                />
                <ProtectedRoute
                  path="/d/account-statement"
                  component={DomesticAccountStatementHub}
                />
                <ProtectedRoute
                  exact
                  path="/d/tracking"
                  component={DomesticLegacyTrackingRedirect}
                />
                <Route path="*" component={NoMatch404} />
              </Switch>
              </ErrorBoundary>
              <ChatWidget />
            </Parent>
          </ChatWidgetProvider>
        </Router>
      </PersistGate>
      </QueryProvider>
    </Provider>
  );
};

export default App;
